/* Speakyti cart cross-sell (V11.1) + page trail (V11.2, at the end) — see snippets/speakyti-cart-upsell.liquid
 * Uses event delegation so it keeps working after Dawn re-renders the drawer.
 *
 * Measurement (Shopify only, no third-party storage):
 *  1. `_source: cart-upsell` line-item property on every item added from the card.
 *  2. Hidden cart attributes (copied onto the order / abandoned checkout):
 *       _spk_upsell_seen    = "<context>:<suggested SKUs>" the first time a card is on screen
 *       _spk_upsell_tapped  = SKU(s) whose "+ Add" was tapped
 *       _spk_upsell_clicked = SKU(s) whose picture or name was tapped (opens the product page)  [V11.1]
 *  3. Shopify custom events for web pixels:
 *       speakyti:upsell_viewed | upsell_add_clicked | upsell_added | upsell_add_failed | upsell_product_clicked
 *
 * V11.1 changes:
 *  - Taps on a card's picture or name are now stored on the cart (`_spk_upsell_clicked`), so an order where
 *    the shopper opened the suggested product's page from the card and added it there can be counted.
 *  - `upsell_viewed` fires once per drawer opening per card content (Dawn re-renders the drawer twice after
 *    an add from a product page, which made it fire twice in V11).
 *  - A short re-check after the drawer opens or re-renders catches views the IntersectionObserver missed
 *    (in V11 the first opening after an add from a product page was sometimes not recorded).
 */
(() => {
  if (window.__spkCartUpsell) return;
  window.__spkCartUpsell = true;

  const addUrl = () => `${(window.routes && window.routes.cart_add_url) || '/cart/add'}.js`;
  const updateUrl = () => `${(window.routes && window.routes.cart_update_url) || '/cart/update'}.js`;

  /* ---------------- measurement helpers ---------------- */

  const split = (value) => (value ? String(value).split(',').filter(Boolean) : []);

  function snapshot(el) {
    const box = el && el.closest('[data-spk-upsell]');
    return {
      box,
      context: box ? box.dataset.spkContext : null,
      suggested_skus: split(box && box.dataset.spkSuggested),
      cart_skus: split(box && box.dataset.spkCart),
      seen: box ? box.dataset.spkSeen || '' : '',
      tapped: box ? box.dataset.spkTapped || '' : '',
      clicked: box ? box.dataset.spkClicked || '' : '',
    };
  }

  function publishEvent(name, snap, extra) {
    try {
      const analytics = window.Shopify && window.Shopify.analytics;
      if (!analytics || typeof analytics.publish !== 'function') return;
      const result = analytics.publish(`speakyti:${name}`, {
        context: snap.context,
        suggested_skus: snap.suggested_skus,
        cart_skus: snap.cart_skus,
        page_path: window.location.pathname,
        ...extra,
      });
      if (result && typeof result.catch === 'function') result.catch(() => {});
    } catch (e) {
      /* analytics must never break the cart */
    }
  }

  // Serialize attribute writes so they never race each other.
  // keepalive lets a write finish even if the page navigates away right after.
  let attributeQueue = Promise.resolve();
  function setCartAttributes(attributes) {
    attributeQueue = attributeQueue
      .then(() =>
        fetch(updateUrl(), {
          method: 'POST',
          keepalive: true,
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ attributes }),
        })
      )
      .catch(() => {});
    return attributeQueue;
  }

  const recordedSeen = new Set(); // per page load, avoids repeat writes before the drawer re-renders
  function recordSeen(snap) {
    if (snap.seen || recordedSeen.size) return; // keep the first thing the shopper saw
    const value = `${snap.context}:${snap.suggested_skus.join('+')}`;
    recordedSeen.add(value);
    if (snap.box) snap.box.dataset.spkSeen = value;
    setCartAttributes({ _spk_upsell_seen: value });
  }

  // Accumulating SKU lists, merged with what the cart already holds.
  function makeAccumulator(attribute, snapKey) {
    const skus = new Set();
    return (snap, sku) => {
      split((snap[snapKey] || '').replace(/\+/g, ',')).forEach((s) => skus.add(s));
      if (!sku || skus.has(sku)) return null;
      skus.add(sku);
      const value = [...skus].join('+');
      if (snap.box) snap.box.dataset[snapKey === 'tapped' ? 'spkTapped' : 'spkClicked'] = value;
      return setCartAttributes({ [attribute]: value });
    };
  }
  const recordTapped = makeAccumulator('_spk_upsell_tapped', 'tapped');
  const recordClicked = makeAccumulator('_spk_upsell_clicked', 'clicked');

  /* ---------------- views: card actually on screen ---------------- */

  // One view per drawer opening per card content. Keyed by content, not DOM node,
  // because Dawn replaces the drawer's DOM when it re-renders.
  let viewedThisOpen = new Set();
  const viewKey = (snap) => `${snap.context}|${snap.suggested_skus.join('+')}|${snap.cart_skus.join('+')}`;

  function countView(el) {
    const drawer = document.querySelector('cart-drawer');
    if (!drawer || !drawer.classList.contains('active') || !el.isConnected) return;
    const snap = snapshot(el);
    const key = viewKey(snap);
    if (viewedThisOpen.has(key)) return;
    viewedThisOpen.add(key);
    publishEvent('upsell_viewed', snap);
    recordSeen(snap);
  }

  // At least half the card inside the viewport.
  function halfOnScreen(el) {
    const r = el.getBoundingClientRect();
    if (!r.height || !r.width) return false;
    const visibleH = Math.min(r.bottom, window.innerHeight) - Math.max(r.top, 0);
    const visibleW = Math.min(r.right, window.innerWidth) - Math.max(r.left, 0);
    return visibleH >= r.height / 2 && visibleW >= r.width / 2;
  }

  // Backup for the IntersectionObserver: right after an add, Dawn re-renders the drawer
  // while it slides in, and the observer can miss the first opening. Re-check shortly after.
  let recheckTimer = null;
  function recheckSoon() {
    clearTimeout(recheckTimer);
    recheckTimer = setTimeout(() => {
      document.querySelectorAll('cart-drawer [data-spk-upsell]').forEach((el) => {
        if (halfOnScreen(el)) countView(el);
      });
    }, 700);
  }

  const observer =
    'IntersectionObserver' in window
      ? new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (entry.isIntersecting) countView(entry.target);
            });
          },
          { threshold: 0.5 }
        )
      : null;

  function observeCards() {
    if (!observer) return;
    document.querySelectorAll('[data-spk-upsell]').forEach((el) => {
      if (el.__spkObserved) return;
      el.__spkObserved = true;
      observer.observe(el);
    });
  }

  function watchDrawer() {
    const drawer = document.querySelector('cart-drawer');
    if (!drawer) return;
    observeCards();
    let wasActive = drawer.classList.contains('active');
    new MutationObserver((mutations) => {
      for (const m of mutations) {
        if (m.type === 'attributes' && m.target === drawer) {
          const isActive = drawer.classList.contains('active');
          if (wasActive && !isActive) viewedThisOpen = new Set(); // closing ends this opening
          wasActive = isActive;
        }
      }
      observeCards();
      if (drawer.classList.contains('active')) recheckSoon();
    }).observe(drawer, { childList: true, subtree: true, attributes: true, attributeFilter: ['class'] });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', watchDrawer);
  else watchDrawer();

  /* ---------------- add to cart ---------------- */

  async function addToCart(button) {
    if (button.getAttribute('aria-disabled') === 'true') return;

    const drawer = document.querySelector('cart-drawer');
    const errorEl = button.closest('.spk-upsell__info')?.querySelector('.spk-upsell__error');
    const sections =
      drawer && typeof drawer.getSectionsToRender === 'function'
        ? drawer.getSectionsToRender().map((section) => section.id)
        : [];
    const snap = snapshot(button);
    const sku = button.dataset.sku || null;

    button.setAttribute('aria-disabled', 'true');
    button.classList.add('is-loading');
    if (errorEl) {
      errorEl.hidden = true;
      errorEl.textContent = '';
    }
    publishEvent('upsell_add_clicked', snap, { product_sku: sku });

    try {
      const response = await fetch(addUrl(), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          id: Number(button.dataset.variantId),
          quantity: 1,
          properties: { _source: 'cart-upsell' },
          sections,
          sections_url: window.location.pathname,
        }),
      });
      const data = await response.json();

      if (!response.ok || data.status) {
        throw new Error(data.description || data.message || 'Could not add this item.');
      }

      publishEvent('upsell_added', snap, { product_sku: sku });
      recordTapped(snap, sku);

      if (drawer && typeof drawer.renderContents === 'function' && data.sections) {
        drawer.classList.remove('is-empty');
        drawer.renderContents(data);
        // renderContents() skips open() when the drawer is already open, so the
        // focus trap is lost after re-render. Put keyboard focus back in the drawer.
        setTimeout(() => {
          const container = document.getElementById('CartDrawer');
          const target = drawer.querySelector('.drawer__inner');
          if (container && typeof trapFocus === 'function') trapFocus(container, target || container);
        }, 60);
      } else {
        await attributeQueue;
        window.location.href = (window.routes && window.routes.cart_url) || '/cart';
        return;
      }

      if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        publish(PUB_SUB_EVENTS.cartUpdate, {
          source: 'product-form',
          productVariantId: button.dataset.variantId,
          cartData: data,
        });
      }
    } catch (error) {
      publishEvent('upsell_add_failed', snap, { product_sku: sku, error: String(error.message).slice(0, 200) });
      recordTapped(snap, sku);
      if (errorEl) {
        errorEl.textContent = error.message;
        errorEl.hidden = false;
      }
      button.removeAttribute('aria-disabled');
      button.classList.remove('is-loading');
    }
  }

  /* ---------------- product link: record, then navigate ---------------- */

  function openProduct(link, event) {
    const snap = snapshot(link);
    const sku = link.dataset.sku || null;
    publishEvent('upsell_product_clicked', snap, { product_sku: sku });
    const write = recordClicked(snap, sku);

    // New tab / window, or nothing new to store: let the browser handle it (keepalive finishes the write).
    const newTab = event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0;
    if (!write || newTab || link.target === '_blank') return;

    // Same tab: wait briefly for the write so it isn't lost, but never more than 800 ms.
    event.preventDefault();
    let done = false;
    const go = () => {
      if (done) return;
      done = true;
      window.location.href = link.href;
    };
    write.then(go, go);
    setTimeout(go, 800);
  }

  /* ---------------- carousel dots ---------------- */

  function activeIndex(track) {
    let best = 0;
    let bestDistance = Infinity;
    [...track.children].forEach((card, index) => {
      const distance = Math.abs(card.offsetLeft - track.scrollLeft);
      if (distance < bestDistance) {
        bestDistance = distance;
        best = index;
      }
    });
    return best;
  }

  function syncDots(track) {
    const dots = track.parentElement?.querySelectorAll('.spk-upsell__dot');
    if (!dots || !dots.length) return;
    const index = activeIndex(track);
    dots.forEach((dot, i) => dot.classList.toggle('is-active', i === index));
  }

  /* ---------------- event delegation ---------------- */

  document.addEventListener('click', (event) => {
    const addButton = event.target.closest('[data-spk-upsell-add]');
    if (addButton) {
      event.preventDefault();
      addToCart(addButton);
      return;
    }

    const productLink = event.target.closest('[data-spk-product-link]');
    if (productLink) {
      openProduct(productLink, event);
      return;
    }

    const dot = event.target.closest('[data-spk-dot]');
    if (dot) {
      const track = dot.closest('[data-spk-upsell]')?.querySelector('.spk-upsell__track');
      const card = track?.children[Number(dot.dataset.spkDot)];
      if (track && card) track.scrollTo({ left: card.offsetLeft, behavior: 'smooth' });
    }
  });

  document.addEventListener(
    'scroll',
    (event) => {
      const track = event.target;
      if (track instanceof Element && track.matches('.spk-upsell__track--carousel')) syncDots(track);
    },
    true
  );
})();

/* ---------------- Page trail (V11.2) ----------------
 * Hidden cart attribute `_spk_trail`, copied onto the order / abandoned checkout like the
 * attributes above, e.g.
 *   "06:31 via instagram.com: puzzle 85s > bath 40s > puzzle 12s"
 * - One line per visit (browser session); earlier visits on the same cart are kept, separated by " || ".
 * - Seconds count only while the page is visible.
 * - Written only when the cart has items (so it never creates carts for browsers who add nothing):
 *   on page load, when the cart changes, when the drawer opens, on Check out, and when the page is hidden.
 * - Pages shown by short names (home, bath, puzzle, bundle, blog:<slug>, cart, shop, search, <page handle>).
 * - Not covered: express checkout straight from a product page (Shop Pay / Apple Pay "Buy now"),
 *   which skips the cart, and the checkout pages themselves.
 */
(() => {
  if (window.__spkTrail) return;
  window.__spkTrail = true;

  const KEY = 'spk_trail_v1';
  const MAX_LEN = 480;
  const updateUrl = () => `${(window.routes && window.routes.cart_update_url) || '/cart/update'}.js`;

  function pageName(path) {
    if (path === '/' || path === '') return 'home';
    if (path.includes('/products/')) {
      if (path.includes('hebrew-alef-bet-bath-letters')) return 'bath';
      if (path.includes('hebrew-wooden-alphabet-puzzle')) return 'puzzle';
      if (path.includes('hebrew-alphabet-bundle')) return 'bundle';
      return `product:${path.split('/products/')[1].slice(0, 20)}`;
    }
    if (path.startsWith('/blogs/') && path.split('/').filter(Boolean).length > 2) return `blog:${path.split('/').pop().slice(0, 24)}`;
    if (path.startsWith('/blogs/')) return 'blog';
    if (path.startsWith('/pages/')) return path.slice(7, 31);
    if (path.startsWith('/collections/')) return 'shop';
    if (path.startsWith('/search')) return 'search';
    if (path.startsWith('/cart')) return 'cart';
    if (path.startsWith('/policies/')) return `policy:${path.slice(10, 30)}`;
    if (path.startsWith('/account')) return 'account';
    return path.slice(0, 24);
  }

  function source() {
    try {
      const params = new URLSearchParams(window.location.search);
      if (document.referrer) {
        const host = new URL(document.referrer).hostname.replace(/^www\./, '');
        if (host && !host.endsWith('speakyti.com') && !host.endsWith('myshopify.com')) return host.slice(0, 24);
      }
      if (params.get('utm_source')) return params.get('utm_source').slice(0, 24);
    } catch (e) {
      /* ignore */
    }
    return 'direct';
  }

  let state = null;
  try {
    state = JSON.parse(sessionStorage.getItem(KEY) || 'null');
  } catch (e) {
    state = null;
  }
  const newVisit = !state || !Array.isArray(state.pages);
  if (newVisit) state = { start: Date.now(), from: source(), base: null, pages: [] };

  // A reload (or the same page twice in a row) adds to the previous entry instead of repeating it.
  const name = pageName(window.location.pathname);
  const last = state.pages[state.pages.length - 1];
  const entry = last && last.n === name ? last : { n: name, t: 0 };
  if (entry !== last) state.pages.push(entry);
  const priorSeconds = entry.t || 0;

  let visibleMs = 0;
  let visibleSince = document.visibilityState === 'visible' ? Date.now() : null;
  const seconds = () => Math.round((visibleMs + (visibleSince ? Date.now() - visibleSince : 0)) / 1000);

  function save() {
    entry.t = priorSeconds + seconds();
    try {
      sessionStorage.setItem(KEY, JSON.stringify(state));
    } catch (e) {
      /* private mode: trail still written to the cart for this page */
    }
  }

  function visitLine() {
    const clock = new Date(state.start).toTimeString().slice(0, 5);
    const pages = state.pages.map((p) => (p.t ? `${p.n} ${p.t}s` : p.n));
    let line = `${clock} via ${state.from}: ${pages.join(' > ')}`;
    while (line.length > MAX_LEN && pages.length > 2) {
      pages.splice(0, 1); // keep the most recent pages
      line = `${clock} via ${state.from}: … > ${pages.join(' > ')}`;
    }
    return line;
  }

  function value() {
    const line = visitLine();
    if (!state.base) return line.slice(0, MAX_LEN);
    const room = MAX_LEN - line.length - 4;
    const base = room > 20 ? state.base.slice(-room) : '';
    return base ? `${base} || ${line}` : line.slice(0, MAX_LEN);
  }

  let itemCount = 0;
  let lastWritten = null;
  function write() {
    save();
    if (!itemCount) return;
    const v = value();
    if (v === lastWritten) return;
    lastWritten = v;
    fetch(updateUrl(), {
      method: 'POST',
      keepalive: true,
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ attributes: { _spk_trail: v } }),
    }).catch(() => {});
  }

  function refreshCart() {
    return fetch(`${(window.routes && window.routes.cart_url) || '/cart'}.js`, { headers: { Accept: 'application/json' } })
      .then((r) => r.json())
      .then((cart) => {
        itemCount = cart.item_count || 0;
        if (newVisit && state.base === null) {
          state.base = (cart.attributes && cart.attributes._spk_trail) || '';
        }
        write();
      })
      .catch(() => {});
  }
  refreshCart();

  // Cart changes (product form, sticky bar, cart offer and drawer quantity buttons all publish this).
  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.cartUpdate, () => setTimeout(refreshCart, 300));
  }

  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') {
      if (!visibleSince) visibleSince = Date.now();
    } else {
      if (visibleSince) visibleMs += Date.now() - visibleSince;
      visibleSince = null;
      write();
    }
  });
  window.addEventListener('pagehide', write);

  // Drawer opening and Check out: write the time spent so far before the shopper leaves.
  const drawer = document.querySelector('cart-drawer');
  if (drawer) {
    new MutationObserver(() => {
      if (drawer.classList.contains('active')) write();
    }).observe(drawer, { attributes: true, attributeFilter: ['class'] });
  }
  document.addEventListener(
    'click',
    (event) => {
      if (event.target.closest('[name="checkout"], #CartDrawer-Checkout, #checkout')) write();
    },
    true
  );
})();
